import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, timer } from 'rxjs';
import { map, repeat, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SessionStorageService, QueryCollectorService } from '@shared/services';
import { ContractExtensionModel, MetaPagingModel      } from '@shared/models';
import { ContractExtension                            } from '@shared/factories';

interface ContractExtensionsResponseModel { contract_extensions: ContractExtensionModel[]; meta: MetaPagingModel; }
interface ContractExtensionResponseModel  { contract_extension:  ContractExtensionModel; }

@Injectable({
  providedIn: 'root'
})
export class ContractExtensionsService {
  static TN_CONTRACT_EXTENSIONS_API:      string = `api/portal/v3/contract_extensions`;
  static ACTIVE_CONTRACT_EXTENSIONS_API:  string = `api/portal/v3/contract_extensions/dashboard`;
  static ARCHIVE_CONTRACT_EXTENSIONS_API: string = `api/portal/v3/contract_extensions/archive`;

  static TN_ASSIGNMENTS_API                   = (assignmentId: number) => `api/portal/v3/assignments/${assignmentId}/contract_extensions`;
  static ARCHIVE_ITEM_CONTRACT_EXTENSIONS_API = (item:         number) => `api/portal/v3/contract_extensions/${item}/review`;

          start$ = new Subject<void>();
  private stop$  = new Subject<void>();

  constructor(
    private http:                  HttpClient,
    private sessionStorageService: SessionStorageService,
    private queryCollectorService: QueryCollectorService
  ) { }

  loadContractExtensionsHistory(assignmentId: number): Observable<ContractExtension[]> {
    return this.http.get<any>(ContractExtensionsService.TN_ASSIGNMENTS_API(assignmentId)).pipe(
      map(res => res?.contract_extensions),
      map(res => res?.map(ce => new ContractExtension(ce)))
    );
  }

  requestContractExtension(data): Observable<ContractExtensionResponseModel> {
    return this.http.post<ContractExtensionResponseModel>(ContractExtensionsService.TN_CONTRACT_EXTENSIONS_API, data);
  }

  forceReload(): void {
    this.forceStop();
    this.start$.next();
  }

  forceStop(): void {
    this.stop$.next();
  }

  get contractExtensions(): Observable<ContractExtension[]> {
    return timer(0).pipe(
      switchMap(() => this.requestContractExtensions('dashboard')),
      takeUntil(this.stop$),
      repeat({delay: () => this.start$})
    );
  }

  getContractExtensionsArchive(): Observable<ContractExtension[]> {
    return this.requestContractExtensions('archive');
  }

  archiveContractExtension(id: number): Observable<ContractExtensionResponseModel> {
    return this.http.put<ContractExtensionResponseModel>(ContractExtensionsService.ARCHIVE_ITEM_CONTRACT_EXTENSIONS_API(id), null).pipe(
      tap(() => this.forceReload())
    );
  }

  private requestContractExtensions(mmType: string): Observable<ContractExtension[]> {
    return this.http.get<ContractExtensionsResponseModel>(`${this.getUrl(mmType)}${this.queryCollectorService.getContractExtensionQuery()}`)
      .pipe(
        tap(res => {
          this.sessionStorageService.changeTotalCount(res?.meta?.paging?.total_count || 0);
          this.sessionStorageService.changeTotalPages(res?.meta?.paging?.total_pages || 0);
        }),
        map(res => res?.contract_extensions.map(ce => new ContractExtension(ce)) || [])
      );
  }

  private getUrl(mmType: string): string {
    if      (mmType === 'dashboard') return ContractExtensionsService.ACTIVE_CONTRACT_EXTENSIONS_API;
    else if (mmType === 'archive'  ) return ContractExtensionsService.ARCHIVE_CONTRACT_EXTENSIONS_API;
  }

}
